/**
 * Field — label + control + hint/error, plus the bare controls themselves.
 *
 * `Field` owns the id wiring: it generates one, passes it down through `children`
 * as a render prop, and points `aria-describedby` at whichever of hint/error is
 * showing. Screens should not hand-roll `<label htmlFor>`.
 *
 *   <Field label="Cron (IST)" hint="Fires in Asia/Kolkata" error={errors.cron} required>
 *     {(p) => <Input {...p} value={cron} onChange={(e) => setCron(e.target.value)} />}
 *   </Field>
 *
 * An error replaces the hint rather than stacking under it, so a form never grows
 * taller as it goes wrong and pushes the submit button off screen.
 */

import { forwardRef, useId } from 'react';
import type {
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react';

/** Props Field injects into the control it wraps. */
export interface FieldControlProps {
  id: string;
  'aria-describedby': string | undefined;
  'aria-invalid': boolean | undefined;
  required: boolean | undefined;
}

export interface FieldProps {
  label: ReactNode;
  /** Render prop gets the wiring; or pass plain children and wire it yourself. */
  children: ReactNode | ((props: FieldControlProps) => ReactNode);
  /** Guidance shown when there is no error. */
  hint?: ReactNode;
  /** Validation message. Truthy = the control renders invalid and the hint hides. */
  error?: string | null;
  required?: boolean;
  /** Show a grey "optional" marker. Use on long forms where most fields are required. */
  optional?: boolean;
  className?: string;
}

export function Field({
  label,
  children,
  hint,
  error,
  required,
  optional,
  className = '',
}: FieldProps) {
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const describedBy = error ? errorId : hint ? hintId : undefined;

  const control: FieldControlProps = {
    id,
    'aria-describedby': describedBy,
    'aria-invalid': error ? true : undefined,
    required: required || undefined,
  };

  return (
    <div className={`field ${className}`.trim()}>
      <label className="field-label" htmlFor={id}>
        {label}
        {required ? (
          <span className="field-required" aria-hidden="true">
            *
          </span>
        ) : null}
        {optional && !required ? <span className="field-optional">optional</span> : null}
      </label>

      {typeof children === 'function' ? children(control) : children}

      {error ? (
        <div className="field-error" id={errorId} role="alert">
          {error}
        </div>
      ) : hint ? (
        <div className="field-hint" id={hintId}>
          {hint}
        </div>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Controls
// ---------------------------------------------------------------------------

/** `size` shadows the rarely-used native attribute on purpose, so it matches Button. */
export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  invalid?: boolean;
  /** Monospace — use for s3 keys, cron expressions, ids and token hashes. */
  mono?: boolean;
  size?: 'sm' | 'md';
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { invalid, mono, size = 'md', className = '', ...rest },
  ref,
) {
  const classes = [
    'input',
    invalid ? 'is-invalid' : '',
    mono ? 'is-mono' : '',
    size === 'sm' ? 'input-sm' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');
  return <input ref={ref} className={classes} {...rest} />;
});

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  invalid?: boolean;
  size?: 'sm' | 'md';
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { invalid, size = 'md', className = '', children, ...rest },
  ref,
) {
  const classes = [
    'select',
    invalid ? 'is-invalid' : '',
    size === 'sm' ? 'select-sm' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <select ref={ref} className={classes} {...rest}>
      {children}
    </select>
  );
});

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  invalid?: boolean;
  mono?: boolean;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { invalid, mono, className = '', ...rest },
  ref,
) {
  const classes = ['textarea', invalid ? 'is-invalid' : '', mono ? 'is-mono' : '', className]
    .filter(Boolean)
    .join(' ');
  return <textarea ref={ref} className={classes} {...rest} />;
});

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label: ReactNode;
  /** Secondary line under the label — good for column-policy explanations. */
  hint?: ReactNode;
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  { label, hint, disabled, className = '', ...rest },
  ref,
) {
  return (
    <label className={`check ${disabled ? 'is-disabled' : ''} ${className}`.trim()}>
      <input ref={ref} type="checkbox" disabled={disabled} {...rest} />
      <span>
        {label}
        {hint ? <span className="check-hint">{hint}</span> : null}
      </span>
    </label>
  );
});

export interface RadioProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label: ReactNode;
  hint?: ReactNode;
}

export const Radio = forwardRef<HTMLInputElement, RadioProps>(function Radio(
  { label, hint, disabled, className = '', ...rest },
  ref,
) {
  return (
    <label className={`check ${disabled ? 'is-disabled' : ''} ${className}`.trim()}>
      <input ref={ref} type="radio" disabled={disabled} {...rest} />
      <span>
        {label}
        {hint ? <span className="check-hint">{hint}</span> : null}
      </span>
    </label>
  );
});

export interface SwitchProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: ReactNode;
  disabled?: boolean;
  /** Accessible name when the visible label is an icon or is omitted. */
  ariaLabel?: string;
  className?: string;
}

/** Use for state that takes effect immediately (schedule enabled/disabled). */
export function Switch({
  checked,
  onChange,
  label,
  disabled = false,
  ariaLabel,
  className = '',
}: SwitchProps) {
  return (
    <label className={`switch ${disabled ? 'is-disabled' : ''} ${className}`.trim()}>
      <input
        type="checkbox"
        role="switch"
        checked={checked}
        disabled={disabled}
        aria-label={ariaLabel}
        onChange={(event) => onChange(event.target.checked)}
      />
      <span className="switch-track" aria-hidden="true">
        <span className="switch-thumb" />
      </span>
      <span>{label}</span>
    </label>
  );
}

/** Responsive row of fields; collapses to one column under ~200px per field. */
export function FieldRow({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <div className={`field-row ${className}`.trim()}>{children}</div>;
}

export default Field;
